import { useState, useEffect, useCallback} from "react";
import axiosInstance from '../../../Config/axiosInstance';
import {Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Button, Grid, 
    Typography, CircularProgress, Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select, Checkbox, FormControl, 
    InputLabel} from '@mui/material';
import { Save, PictureAsPdf, GetApp, Assessment} from '@mui/icons-material';
import './ProjectReports.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from 'exceljs';
import {saveAs} from "file-saver";
import logoImg from '../../../Images/logo_pmpc.png'


const ProjectReport = () => {
    const [filters, setFilters] = useState({
        month: (new Date().getMonth() + 1).toString().padStart(2, '0'),
        year: new Date().getFullYear(),
        status: 'ALL', 
        projectCode: ['ALL']
    });

    const [statusOptions, setStatusOptions] = useState([
        { value: 'ALL', label: 'All Statuses' } 
    ]);
    const [projectCodeOptions, setProjectCodeOptions] = useState([
        { value: 'ALL', label: 'All Project Codes' }
    ]);
    
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState('');
    const [reportGenerated, setReportGenerated] = useState(false);

    const monthOptions = [
        { value: '01', label: 'January' }, { value: '02', label: 'February' }, 
        { value: '03', label: 'March' }, { value: '04', label: 'April' }, 
        { value: '05', label: 'May' }, { value: '06', label: 'June' }, 
        { value: '07', label: 'July' }, { value: '08', label: 'August' }, 
        { value: '09', label: 'September' }, { value: '10', label: 'October' },
        { value: '11', label: 'November' }, { value: '12', label: 'December' }
    ];

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - 2 + i);
    
    // functie pentru incarcarea optiunilor
    const loadFilterOptions = useCallback(async (month, year, status = 'ALL') => {
        setLoadingOptions(true);
        try {
            const response = await axiosInstance.get('/project-report/', {
                params: {
                    month: month,
                    year: year,
                    status: status,
                    code: 'ALL'
                }
            });

            const data =  response.data;

            // actualizeaza optiunile de status
            if(data.statuses && data.statuses.length > 0) {
                const statusOpts = data.statuses.map(status => ({
                    value: status, 
                    label: status === 'ALL' ? 'All Statuses' : status
                }));
                setStatusOptions(statusOpts)
            }

            if(data.codes && data.codes.length > 0) {
                const projectOpts = [
                    { value: 'ALL', label: 'All Project Codes' },
                    ...data.codes
                        .filter(code => code !== 'ALL')
                        .map(code => ({
                            value: code, 
                            label: code
                        }))
                ];
                setProjectCodeOptions(projectOpts);
            } else {
                setProjectCodeOptions([{ value: 'ALL', label: 'All Project Codes' }]);
            }
        } catch (err) {
            console.error('Error fetching filter options: ', err);
            setError('Failed to load filter options');
            setStatusOptions([{value: 'ALL', label: 'All Statuses'}]);
            setProjectCodeOptions([{ value: 'ALL', label: 'All Project Codes' }]);
        } finally {
            setLoadingOptions(false);
        }
    }, []);

    useEffect(() => {
       loadFilterOptions(filters.month, filters.year, filters.status);
    }, [filters.month, filters.year, filters.status, loadFilterOptions]);

    const handleFilterChange = (field, value) => {
        if (field === 'projectCode') {
            const allCodes = projectCodeOptions
                .filter(opt => opt.value !== 'ALL')
                .map(opt => opt.value);

            // la click pe ALL
            if (value.includes('ALL')) {
                // daca ALL era deja selectat, deselectează tot
                if (filters.projectCode.includes('ALL')) {
                    setFilters(prev => ({ ...prev, projectCode: [] }));
                } 
                // altfel selecteaza tot
                else {
                    setFilters(prev => ({ ...prev, projectCode: ['ALL'] }));
                }
                return;
            }

            // selectie manuala
            if (value.length === allCodes.length) {
                // toate bifate manual atunci marchează ALL
                setFilters(prev => ({ ...prev, projectCode: ['ALL'] }));
            } else {
                setFilters(prev => ({ ...prev, projectCode: value }));
            }

            setReportGenerated(false);
            return;
        }

        setFilters(prev => ({ ...prev, [field]: value }));
        setReportGenerated(false);

        if (field === 'status') {
            setFilters(prev => ({
                ...prev,
                status: value,
                projectCode: ['ALL']
            }));
            setReportGenerated(false);
            return;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (filters.projectCode.length === 0) {
            setError('Please select at least one Project Code.');
            setReportGenerated(false);
            setReportData(null);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const params = {
                month: filters.month,
                year: filters.year,
                status: filters.status,
            };

            if (filters.projectCode.includes('ALL')) {
                params.code = 'ALL';
            } else if (filters.projectCode.length === 1) {
                params.code = filters.projectCode[0];
            } else {
                params.code = 'MULTIPLE';
                params.multiple_codes = filters.projectCode.join(',');
            }

            const response = await axiosInstance.get('/project-report/', { params });
            setReportData(response.data);
            setReportGenerated(true);

        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate report');
            setReportData(null);
            setReportGenerated(false);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatNumber = (num) => {
        if (num == null || num === undefined) return '0.00';
        return new Intl.NumberFormat('en-EN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    const exportToExcel = async (data = null, customTitle = null) => {
        if (!reportData || !reportData.projects || reportData.projects.length === 0) {
            alert('No data to export!');
            return;
        }
        
        const exportData = data || reportData;
        const title = customTitle || `Project Report - ${monthOptions.find(m => m.value === filters.month)?.label} ${filters.year}`;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Report');
        
        //logo
        try {
            sheet.mergeCells('A1:A4');
            const logo = workbook.addImage({
                buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
                extension: 'png',
            });
            sheet.addImage(logo, {
                tl: { col: 0, row: 0 },
                br: { col: 1, row: 4 }
            });
        } catch (error) {
            console.warn('Logo could not be loaded:', error);
        }

        //titlu 
        sheet.mergeCells('B1:D4');
        const titleCell = sheet.getCell('B1');
        titleCell.value = title;
        titleCell.font = { size: 14, bold: true };
        titleCell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true
        };

        //rand liber dupa titlu
        sheet.insertRow(5, []);

        //header pentru primul tabel
        const headerRowNumber = 6;
        const headers = ["Project Code", "Month", "Project Expense", "Commercial Income"];
        sheet.getRow(headerRowNumber).height = 30;
        
        const headerRow = sheet.getRow(headerRowNumber);
        headerRow.values = headers;
        
        //stil pentru header
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE8E8E8' }
            };
        });

        //date lunare
        exportData.projects.forEach((project, index) => {
            const dataRowNumber = headerRowNumber + 1 + index;
            const row = sheet.getRow(dataRowNumber);
            row.height = 20;
            row.values = [
                project.code,
                filters.month,
                project.admin_cost_monthly || 0,
                project.invoice_cost_monthly || 0
            ];
            
            //formatare numerica
            row.getCell(3).numFmt = '#,##0.00';
            row.getCell(4).numFmt = '#,##0.00';           
            row.getCell(2).alignment = { horizontal: 'right' };
            row.getCell(3).alignment = { horizontal: 'right' };
            row.getCell(4).alignment = { horizontal: 'right' };
        });

        //spatiu intre tabele
        const spaceAfterMonthly = headerRowNumber + exportData.projects.length + 1;
        sheet.insertRow(spaceAfterMonthly, []);
        sheet.insertRow(spaceAfterMonthly + 1, []);

        //header pentru al doilea tabel
        const yearlyHeaderRow = spaceAfterMonthly + 2; 
        const yearlyHeaders = ["Project Code", "Year", "Yearly Project Expense", "Yearly Commercial Income"]; 

        sheet.getRow(yearlyHeaderRow).height = 30;
        
        const yearlyHeaderRowObj = sheet.getRow(yearlyHeaderRow);
        yearlyHeaderRowObj.values = yearlyHeaders;
        yearlyHeaderRowObj.font = { bold: true };
        yearlyHeaderRowObj.eachCell((cell) => {
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE8E8E8' }
            };
        });

        //date anuale
        exportData.projects.forEach((project, index) => {
            const dataRowNumber = yearlyHeaderRow + 1 + index;
            const row = sheet.getRow(dataRowNumber);
            row.height = 20;
            row.values = [
                project.code, 
                filters.year,
                project.admin_cost_yearly || 0,
                project.invoice_cost_yearly || 0
            ];

            //formatare numerice
            row.getCell(3).numFmt = '#,##0.00';
            row.getCell(4).numFmt = '#,##0.00';
            row.getCell(3).alignment = { horizontal: 'right' };
            row.getCell(4).alignment = { horizontal: 'right' };
        });

        //auto-width
        sheet.columns.forEach((column, columnIndex) => {
            let maxLength = 0;
            column.eachCell({includeEmpty: true}, (cell) =>{
                let cellValue = cell.value?.toString() || '';
                if(cell.numFmt === '0.00' && !isNaN(cell.value)){
                    cellValue = parseFloat(cell.value || 0).toFixed(2);
                }
                maxLength = Math.max(maxLength, cellValue.length);
            });

            //factor pentru auto-width
            if(columnIndex === 0) {
                column.width = Math.max((maxLength + 10) * 1.1, 12);
            } else {
                column.width = Math.max((maxLength + 1) * 0.75, 12);
            }
        });

        //genereaza si salveaza fisierul
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });

        const fileName = `ProjectReport_${filters.month}_${filters.year}.xlsx`;
        saveAs(blob, fileName);
    };

    const exportPDF = async () => {
        if (!reportData || !reportData.projects || reportData.projects.length === 0) {
            alert('No data to export!');
            return;
        }
        
        //pdf portrait
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const logo = "/logo_pmpc.png";

        //logo
        pdf.addImage(logo, "PNG", 8, 3, 28, 8);

        //dimensiuni elemente decorative
        const triangleWidth = 20;
        const totalWidth = pdfWidth * 0.25;
        const barWidth = totalWidth - triangleWidth;

        const barHeight = 1.2;     // bara scurtă + triunghi
        const secondBarHeight = 2; // bara lungă
        const barTop = 15;
        const barBottom = barTop + barHeight;

        //bara scurta
        pdf.setFillColor(23, 52, 88);
        const barOverlap = 1;
        pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

        //triunghi
        pdf.triangle(
        barWidth, barBottom,                 
        barWidth + triangleWidth, barBottom,  
        barWidth, barTop,                   
        "F"
        );

        //suprapunere 
        const overlap = 0.6;
        const headerLongBarTop = barBottom - overlap;

        //bara lunga full-width (header)
        pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

        const dottedLineY = headerLongBarTop + secondBarHeight;
        //titlu
        const selectedMonthName = monthOptions.find(m => m.value === filters.month)?.label || "";
        const reportTitle = `Project Report - ${selectedMonthName} ${filters.year}`;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(reportTitle, pdfWidth / 2, dottedLineY + 12, {align: "center"});

        const contentTop = dottedLineY + 20;
        let currentY = contentTop;

        const columnStyles = {
            0: { cellWidth: 45 },
            1: { cellWidth: 20, halign: 'right' },
            2: { cellWidth: 50, halign: 'right' }, 
            3: { cellWidth: 50, halign: 'right' }  
        };

        //date lunare
        const monthlyTableHeaders = [
            ["Project Code", "Month", "Project Expense", "Commercial Income"]
        ];

        const monthlyTableData = reportData.projects.map(project => {
            return [
                project.code,
                filters.month,
                formatNumber(project.admin_cost_monthly),
                formatNumber(project.invoice_cost_monthly)
            ];
        });

        //adauga primul tabel
        autoTable(pdf, {
            startY: currentY,
            head: monthlyTableHeaders,
            body: monthlyTableData,
            styles: {
                fontSize: 9, 
                cellPadding: 2,
                overflow: "linebreak",
                lineColor: [180, 180, 180],
                lineWidth: 0.25,
                valign: "middle",
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [232, 232, 232],
                textColor: 0, 
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: columnStyles, 
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: {left: 10, right: 10}, 
            tableWidth: 'auto',
            theme: 'grid',
            didDrawPage: function(data) {
                if (data.cursor) {
                    currentY = data.cursor.y;
                }
            }
        });
        
        currentY = pdf.lastAutoTable.finalY + 15;

        //verifica daca mai este spatiu pe al doilea tabel
        if (currentY > pdfHeight - 80) {
            pdf.addPage();
            currentY = 20;
        }

        //date anuale
        const yearlyTableHeaders = [
            ["Project Code", "Year", "Yearly Project Expense", "Yearly Commercial Income"]
        ];

        const yearlyTableData = reportData.projects.map(project => {
            return [
                project.code, 
                filters.year,
                formatNumber(project.admin_cost_yearly),
                formatNumber(project.invoice_cost_yearly)
            ];
        });

        //al doilea tabel cu acelasi latimi
        autoTable(pdf, {
            startY: currentY,
            head: yearlyTableHeaders,
            body: yearlyTableData,
            styles: {
                fontSize: 9,
                cellPadding: 2,
                overflow: "linebreak",
                lineColor: [180, 180, 180],
                lineWidth: 0.25,
                valign: "middle",
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [232, 232, 232],
                textColor: 0,
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: columnStyles,  
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: {left: 10, right: 10},
            tableWidth: 'auto',
            theme: 'grid',
        });

        //footer pentru toate paginile
        const totalPages = pdf.internal.getNumberOfPages();
        
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            pdf.setFillColor(23, 52, 88);

            const bottomOffset = 12; 
            const longBarTop = pdfHeight - bottomOffset; 

            //suprapunere
            const verticalOverlapFooter = 0.6;

            //bara lunga jos
            pdf.rect(
            0,
            longBarTop - verticalOverlapFooter + 0.1,
            pdfWidth,
            secondBarHeight,
            "F"
            );

            //bara scurta + triunghi
            const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
            const shortBarBottom = shortBarTop + barHeight;

            //bara scurta 
            const barOverlapFooter = 1;
            pdf.rect(
            0,
            shortBarTop,
            barWidth + barOverlapFooter,
            barHeight,
            "F"
            );

            //triunghiul din partea dreapta jos
            pdf.triangle(
            barWidth, shortBarTop,                    
            barWidth + triangleWidth, shortBarTop,        
            barWidth, shortBarBottom,                      
            "F"
            );

            //pagina curenta/ total pagini
            pdf.setFontSize(7);
            pdf.setFont("helvetica", "normal");
            pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
            const today = new Date();
            const currentDate = today.toLocaleDateString("ro-RO");
            pdf.text(`${currentDate} `, pdfWidth - 30, pdfHeight - 3);

            pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
        }

        //save pdf
        pdf.save(`ProjectReport_${filters.month}_${filters.year}.pdf`);
    };

    return (
        <Container maxWidth={false} disableGutters className="proj-report project-report">
            <Container maxWidth={false} disableGutters className="project-container">
                <Typography variant="h4" gutterBottom marginTop="20px">
                    Project Report
                </Typography>
                
                <Paper className="filters-paper" sx={{ p: 2 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={9}>
                                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                                    <TextField
                                        select
                                        label="Month"
                                        value={filters.month}
                                        onChange={(e) => handleFilterChange('month', e.target.value)}
                                        required
                                        size="small"
                                        className="month-select"
                                        sx={{ minWidth: 120 }}
                                    >
                                        {monthOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        select 
                                        label="Year"
                                        value={filters.year}
                                        onChange={(e) => handleFilterChange('year', e.target.value)}
                                        required
                                        size="small"
                                        className="year-select"
                                    >
                                        {yearOptions.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    
                                    <TextField
                                        select 
                                        label="Status"
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        size="small"
                                        className="status-select"
                                        disabled={loadingOptions}
                                    >
                                        {statusOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <FormControl 
                                        size="small" 
                                        className="project-code-select"
                                        sx={{ minWidth: 180 }}
                                        disabled={loadingOptions || projectCodeOptions.length === 0}
                                    >
                                        <InputLabel>Project Code</InputLabel>
                                        <Select
                                            multiple
                                            value={filters.projectCode}
                                            onChange={(e) => handleFilterChange('projectCode', e.target.value)}
                                            input={<OutlinedInput label="Project Code" />}
                                            renderValue={(selected) => {
                                                if (selected.length === 0) {
                                                    return 'Select Project Codes';
                                                }
                                                if (selected.includes('ALL')) {
                                                    return 'All Project Codes';
                                                }
                                                if (selected.length > 1) {
                                                    return `${selected.length} project codes selected`;
                                                }
                                                return selected.join(', ');
                                            }}
                                             MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                    width: 250,
                                                    '& .MuiMenuItem-root': {
                                                        fontSize: '0.75rem',
                                                        paddingTop: 0.25,
                                                        paddingBottom: 0.25,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    },
                                                    '& .MuiCheckbox-root': {
                                                        padding: '2px',
                                                        marginRight: '4px',
                                                    },
                                                    },
                                                },
                                            }}
                                            >
                                            {projectCodeOptions.length > 0 ? (
                                                projectCodeOptions.map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        <Checkbox
                                                            checked={
                                                                option.value === 'ALL'
                                                                    ? filters.projectCode.includes('ALL')
                                                                    : filters.projectCode.includes('ALL') ||
                                                                    filters.projectCode.includes(option.value)
                                                            }
                                                        />
                                                        <ListItemText primary={option.label} />
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>
                                                    <ListItemText 
                                                        primary={loadingOptions ? "Loading..." : "No project codes available"} 
                                                    />
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>

                                    <Button 
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        className="generate-button"
                                        startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                                        disabled={loadingOptions || loading}
                                    >
                                        {loading ? 'Generating...' : 'Generate Report'}
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Grid container spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleClick}
                                            className="save-button"
                                            startIcon={<Save />}
                                            disabled={!reportGenerated}
                                        >
                                            Save As
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Menu 
                                    anchorEl={anchorEl} 
                                    open={Boolean(anchorEl)} 
                                    onClose={handleClose}
                                >
                                    <MenuItem onClick={() => {
                                        exportPDF();
                                        handleClose();
                                    }}>
                                        <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save as PDF</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() =>{
                                        exportToExcel();
                                        handleClose();
                                    }}>
                                        <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save as Excel</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {filters.projectCode.length > 1 && !filters.projectCode.includes('ALL') && (
                            <Box mt={2}>
                                <Typography variant="caption" color="text.secondary">
                                    Selected Project Codes:
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                    {filters.projectCode.map(code => (
                                        <Box
                                            key={code}
                                            sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 1,
                                                backgroundColor: '#e3f2fd',
                                                fontSize: '0.75rem',
                                                fontWeight: 500
                                            }}
                                        >
                                            {code}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </form>
                </Paper>

                {reportData && reportGenerated && (
                    <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                    <Paper sx={{ p: 2 }}>
                        {reportData.count > 0 && reportData.projects && reportData.projects.length > 0 ? (
                        <TableContainer className="report-table-container">
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow className="table-header">
                                <TableCell className="table-header-cell">Project Code</TableCell>
                                <TableCell className="table-header-cell" align="center">Month</TableCell>
                                <TableCell className="table-header-cell" align="center">Project Expense</TableCell>
                                <TableCell className="table-header-cell" align="center">Commercial Income</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.projects.map((project) => (
                                <TableRow 
                                    key={project.code}
                                    hover
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell sx={{ width: '25%' }}>{project.code}</TableCell>
                                    <TableCell sx={{ width: '25%' }} align="center">{filters.month}</TableCell>
                                    <TableCell sx={{ width: '25%' }} align="center">{formatNumber(project.admin_cost_monthly)}</TableCell>
                                    <TableCell sx={{ width: '25%' }} align="center">{formatNumber(project.invoice_cost_monthly)}</TableCell>
                                </TableRow>
                                ))}
                            </TableBody>
                            </Table>
                        </TableContainer>
                        ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No projects found for the selected criteria.
                        </Alert>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2 }}>
                        {reportData.count > 0 && reportData.projects && reportData.projects.length > 0 ? (
                        <TableContainer className="report-table-container">
                            <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow className="table-header">
                                <TableCell className="table-header-cell">Project Code</TableCell>
                                <TableCell className="table-header-cell" align="center">Year</TableCell>
                                <TableCell className="table-header-cell" align="center">Yearly Project Expense</TableCell>
                                <TableCell className="table-header-cell" align="center">Yearly Commercial Income</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.projects.map((project) => (
                                <TableRow
                                    key={project.code}
                                    hover
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                    <TableCell sx={{ width: '25%' }}>{project.code}</TableCell>
                                    <TableCell sx={{ width: '25%' }} align="center">{filters.year}</TableCell>
                                    <TableCell sx={{ width: '25%' }} align="center">{formatNumber(project.admin_cost_yearly)}</TableCell>
                                    <TableCell sx={{ width: '25%' }} align="center">{formatNumber(project.invoice_cost_yearly)}</TableCell>
                                </TableRow>
                                ))}
                            </TableBody>
                            </Table>
                        </TableContainer>
                        ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            No yearly project data available.
                        </Alert>
                        )}
                    </Paper>
                    </Box>
                    </>
                )}
            </Container>
            {reportData && reportGenerated && (
                <Box className="report-footer">
                    <Typography variant="caption" className="footer-text">
                        Generated on {reportData.report_date || new Date().toLocaleString('ro-RO')} |{' '}
                        {monthOptions.find(m => m.value === filters.month)?.label} {filters.year} |{' '}
                        Status: {filters.status === 'ALL' ? 'All Statuses' : filters.status} |{' '}
                        Project Codes: {filters.projectCode.includes('ALL') 
                            ? 'All Project Codes'
                            : filters.projectCode.length > 0
                                ? (filters.projectCode.length > 3 
                                    ? `${filters.projectCode.length} codes selected`
                                    : filters.projectCode.join(', '))
                                : 'None selected'
                        }
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default ProjectReport;